import { useEffect, useRef, useState, type ReactNode } from "react";
import { useAgent } from "@copilotkit/react-core/v2";
import { Loader2, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { getChatHistory, type ChatHistoryMessage } from "@/services/chatHistoryService";
import { scrollChatToBottom } from "./chatScroll";
import { mapHistoryToAgentMessages } from "./mapHistoryToAgentMessages";

/**
 * Empty-chat entry point. CopilotKit mounts a welcome screen ONLY when the
 * chat surface is open AND the in-memory conversation is empty — i.e. the user
 * just opened the assistant on a fresh page load. On that moment we RESTORE
 * history: fetch the user's stored conversation and, if any, `setMessages` it so
 * the sidebar continues where they left off (the FE buffer is per-page-load;
 * the BFF holds the durable history). Otherwise we land on the idle welcome
 * (input + suggestions) and let the user choose what to ask.
 *
 * Doing this here (rather than restoring on provider mount) only fetches when
 * the user actually opens the assistant.
 *
 * IMPORTANT: the welcome screen OWNS the chat input + suggestion pills (passed
 * in as slot elements) — we MUST render them or the empty state is a dead end.
 */

const HISTORY_LIMIT = 50;

interface ProactiveWelcomeProps {
  input?: ReactNode;
  suggestionView?: ReactNode;
}

export function CopilotProactiveWelcome({ input, suggestionView }: ProactiveWelcomeProps) {
  const { user } = useAuth();
  const { agent } = useAgent({ agentId: "default" });
  const ranRef = useRef(false);
  // briefKey bumps when the buffer empties (new chat), re-triggering the effect
  // even when the component stays mounted (CopilotKit hides/shows via CSS).
  const [briefKey, setBriefKey] = useState(0);
  const [phase, setPhase] = useState<"working" | "idle">("working");

  // When the message buffer goes to zero (new chat cleared it), reset the
  // "already ran" gate and bump briefKey so the main effect re-fires.
  const prevMsgCount = useRef<number | null>(null);
  useEffect(() => {
    if (!agent) return;
    const count = agent.messages.length;
    if (prevMsgCount.current !== null && prevMsgCount.current > 0 && count === 0) {
      ranRef.current = false;
      setPhase("working");
      setBriefKey((k) => k + 1);
    }
    prevMsgCount.current = count;
  }, [agent, agent?.messages.length]);

  useEffect(() => {
    if (ranRef.current || !agent) return;
    // Don't disturb an in-flight run or a buffer that already has messages.
    if (agent.messages.length > 0 || agent.isRunning) {
      setPhase("idle");
      return;
    }
    ranRef.current = true;
    let cancelled = false;

    void (async () => {
      // 1) Restore prior conversation, if any (most recent page).
      let history: ChatHistoryMessage[] = [];
      try {
        history = (await getChatHistory(1, HISTORY_LIMIT)).messages;
      } catch {
        /* history fetch failed — fall through to the idle path */
      }
      if (cancelled || agent.messages.length > 0 || agent.isRunning) return;

      if (history.length > 0) {
        agent.setMessages(mapHistoryToAgentMessages(history));
        setPhase("idle"); // welcome unmounts as the buffer fills with restored messages
        scrollChatToBottom(); // land on the newest message, where the user left off
        return;
      }

      // 2) No history → land on the idle welcome screen (input + suggestions).
      setPhase("idle");
      return;
    })();

    return () => {
      cancelled = true;
    };
  }, [agent, briefKey]);

  const firstName = user?.fullName?.split(" ").slice(-1)[0] ?? "there";
  const busy = phase === "working";
  const heading = busy ? `Hi ${firstName}, catching up…` : `Hi ${firstName} — how can I help?`;
  const sub = busy
    ? "Loading your conversation."
    : "Ask about your priorities, OKRs, or your team.";

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10">
          {busy ? (
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          ) : (
            <Sparkles className="h-5 w-5 text-primary" />
          )}
        </div>
        <p className="text-sm font-medium text-foreground">{heading}</p>
        <p className="max-w-xs text-xs text-muted-foreground">{sub}</p>
      </div>
      {/* Slot-provided suggestion pills + input — required so the user can type. */}
      {suggestionView}
      {input}
    </div>
  );
}
